import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { usePortfoliosCRUD } from "@/pages/PortfolioBuilder/api/usePortfoliosCRUD";
import {
  useTagsWatchlistsCRUD,
  type TagsWatchlist,
} from "@/shared/api/pref-storage/tags-watchlists/useTagsWatchlistsCRUD";
import {
  validateKeywordsSize,
  validateTermsTotal,
  MAX_KEYWORDS_COUNT,
  MAX_KEYWORDS_TOTAL_CHARS,
  MAX_TERMS_TOTAL,
} from "@/pages/PortfolioBuilder/keywordLimits";
import { parseCsvKeywords } from "@/pages/PortfolioBuilder/parsePastedList";
import { resolveTerms } from "@/shared/utils/resolveTerms";
import {
  clearPrebuiltOverride,
  setPrebuiltOverride,
  subscribeToPrebuiltOverrides,
  getPrebuiltOverridesSnapshot,
} from "@/pages/PortfolioBuilder/prebuiltWatchlists";
import { menuCategories } from "@/widget-components/newsquawk-headlines/models/categories";
import { getTagsByIds } from "@/shared/api/newsquawk-headlines";
import type { Portfolio } from "@/types/Portfolio";
import type { CategoryQuery } from "@/widget-components/newsquawk-headlines/models/CategoryQuery";
import {
  emptyNotifications,
  type FilterNotifications,
} from "@/types/notifications";
import toast from "react-hot-toast";
import { trackFeatureUsage } from "@/utils/gtm";
import useAuth from "@/hooks/useAuth";
import { useDemoGuideStore } from "@/demo-guide/useDemoGuideStore";
import { formatListName } from "@/shared/utils/formatListName";
import type { ResolvedTerm } from "@/shared/utils/resolveTerm";

type Category = { title: string; id: number | null };

/** Same members, order aside. */
const sameMembers = (a: string[], b: string[]) =>
  a.length === b.length && a.every((x) => b.includes(x));

/**
 * Which storage a watchlist lives in — NOT a choice the user makes.
 *
 * Tag watchlists really are a separate resource with their own API, so the
 * distinction has to survive somewhere. It is no longer allowed to reach the
 * user: nothing is titled by it, and "ticker" vs "keyword" is now decided by
 * what the tag lookup makes of each entry rather than asked up front.
 */
export type WatchlistCreatorMode = "ticker" | "keyword" | "tag";

/**
 * A saved list of any kind, for the rail to hand back on click.
 *
 * "Your watchlists" listed only portfolios, so a tag or combined watchlist the
 * user made was missing from the one place named after them. They are all
 * watchlists to the reader; the kind exists here only so the click can be
 * routed to the right editor.
 */
export type EditableWatchlist =
  | { kind: "watchlist"; portfolio: Portfolio }
  | { kind: "tag"; watchlist: TagsWatchlist }
  | { kind: "combined"; query: CategoryQuery };

/** What a save produced, so the caller can select and preview it. */
export interface SavedWatchlist {
  kind: "watchlist" | "tag" | "combined";
  id: number;
  /**
   * Tag watchlists only: the tags as saved. The cached record is either
   * missing (a create) or stale (an edit) until the refetch lands, and a
   * preview built from it shows the wrong headlines, or none.
   */
  tags?: string[];
}

/**
 * Pre-fill for CREATE mode — seeds the form without entering edit mode, so
 * saving still creates a new resource. From the concierge, or a Newsquawk
 * list opened from the library.
 */
export interface CreatorDraft {
  name?: string;
  tickers?: string[];
  keywords?: string[];
  tags?: string[];
  /**
   * Name of the Newsquawk list this was opened from. The form then says it is
   * customising that list rather than building from scratch.
   */
  basedOn?: string;
  /**
   * The library watchlist itself, when its colour and priority can be kept
   * for this user without copying it (portfolios; tag watchlists have
   * neither). Those two then save as the user's own override of the list.
   */
  libraryId?: number;
  /** The list's colour and priority as the user currently sees them. */
  color?: string;
  priority?: number;
}

export interface UseWatchlistCreatorOptions {
  mode: WatchlistCreatorMode;
  /** Existing portfolio to edit (omit for create mode) */
  editingPortfolio?: Portfolio | null;
  /** Existing tag watchlist to edit (tag mode only; omit for create mode) */
  editingTagWatchlist?: TagsWatchlist | null;
  draft?: CreatorDraft | null;
  /** Receives what was saved, so callers can select/preview it rather than
   *  navigating away from the thing the user just made. */
  onCreated?: (saved?: SavedWatchlist) => void;
  onDeleted?: () => void;
}

// Synchronously hydrate selectedTags from a list of tag IDs using menuCategories;
// unknown IDs get a placeholder title that callers can refine via getTagsByIds.
function hydrateSelectedTags(tagIds: string[]): {
  initial: Category[];
  unknownIds: string[];
} {
  const initial: Category[] = [];
  const unknownIds: string[] = [];
  for (const tagId of tagIds) {
    let found = false;
    for (const cat of menuCategories) {
      const item = cat.items.find((i) => String(i.id) === tagId);
      if (item) {
        initial.push({ title: item.title, id: item.id });
        found = true;
        break;
      }
    }
    if (!found) {
      initial.push({ title: `Tag #${tagId}`, id: Number(tagId) });
      unknownIds.push(tagId);
    }
  }
  return { initial, unknownIds };
}

export function useWatchlistCreator(options: UseWatchlistCreatorOptions) {
  const { authenticated } = useAuth();
  const markWatchlistCreated = useDemoGuideStore((s) => s.markWatchlistCreated);

  const { createMutation, updateMutation, deleteMutation, portfoliosQuery } =
    usePortfoliosCRUD();
  const portfolios = portfoliosQuery.data || [];
  const {
    createMutation: tagCreateMutation,
    updateMutation: tagUpdateMutation,
    deleteMutation: tagDeleteMutation,
  } = useTagsWatchlistsCRUD();

  const isEditing = !!options.editingPortfolio || !!options.editingTagWatchlist;
  const editId =
    options.editingPortfolio?.id ?? options.editingTagWatchlist?.id;

  const initialSelectedTags = useMemo(
    () =>
      options.editingTagWatchlist
        ? hydrateSelectedTags(options.editingTagWatchlist.tags).initial
        : [],
    [options.editingTagWatchlist]
  );

  // Form state — pre-populated when editing
  const [name, setName] = useState(
    options.editingPortfolio?.name ?? options.editingTagWatchlist?.name ?? ""
  );
  const [tickers, setTickers] = useState<string[]>(
    options.editingPortfolio?.tickers ?? []
  );
  const [keywords, setKeywords] = useState<string[]>(
    options.editingPortfolio?.keywords ?? []
  );
  /**
   * term -> tag id, for terms the tag service recognised.
   *
   * Keyed by term so dropping a term drops its tag, and so a topic pairing can
   * be removed without removing the word itself. Saved to Portfolio.tags.
   */
  const [termTags, setTermTags] = useState<Record<string, string>>({});
  const [selectedTags, setSelectedTags] =
    useState<Category[]>(initialSelectedTags);
  const [color, setColor] = useState(
    options.editingPortfolio?.color ?? "#FF0000"
  );
  const [priority, setPriority] = useState(
    options.editingPortfolio?.priority ?? 1
  );
  const [notifications, setNotifications] = useState<FilterNotifications>(
    options.editingPortfolio?.notifications ??
      options.editingTagWatchlist?.notifications ??
      emptyNotifications()
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  /** A batch is being looked up against the tag service (CSV upload). */
  const [isResolvingTerms, setIsResolvingTerms] = useState(false);

  /**
   * Desk fields. Inert for everyone else — the UI that sets them is gated on
   * `useIsDeskUser()`, and a payload without them behaves exactly as before.
   *
   * `publishToLibrary` is create-only by design: the server ignores
   * `owner_type` on PATCH, so a list cannot move into or out of the Newsquawk
   * library after it exists. Seeded from the record being edited so the
   * builder can show what a list already is without offering to change it.
   */
  const editingOwnerType =
    options.editingPortfolio?.owner_type ??
    options.editingTagWatchlist?.owner_type;
  const [publishToLibrary, setPublishToLibrary] = useState(
    editingOwnerType === "NEWSQUAWK"
  );
  const [description, setDescription] = useState(
    options.editingPortfolio?.description ??
      options.editingTagWatchlist?.description ??
      ""
  );
  const [categories, setCategories] = useState<string[]>(
    options.editingPortfolio?.categories ??
      options.editingTagWatchlist?.categories ??
      []
  );
  const [whenToUse, setWhenToUse] = useState(
    options.editingPortfolio?.when_to_use ??
      options.editingTagWatchlist?.when_to_use ??
      ""
  );

  // Reset form to defaults (or to editing portfolio/tag-watchlist values)
  const reset = useCallback(() => {
    const p = options.editingPortfolio;
    const t = options.editingTagWatchlist;
    if (p) {
      setName(p.name);
      setTickers(p.tickers);
      setKeywords(p.keywords ?? []);
      setSelectedTags([]);
      setColor(p.color);
      setPriority(p.priority);
      setNotifications(p.notifications);
      setPublishToLibrary(p.owner_type === "NEWSQUAWK");
      setDescription(p.description ?? "");
      setWhenToUse(p.when_to_use ?? "");
      setCategories(p.categories ?? []);
    } else if (t) {
      setName(t.name);
      setTickers([]);
      setKeywords([]);
      setSelectedTags(hydrateSelectedTags(t.tags).initial);
      setColor("#FF0000");
      setPriority(1);
      setNotifications(t.notifications ?? emptyNotifications());
      setPublishToLibrary(t.owner_type === "NEWSQUAWK");
      setDescription(t.description ?? "");
      setWhenToUse(t.when_to_use ?? "");
      setCategories(t.categories ?? []);
    } else if (options.draft) {
      setName(options.draft.name ?? "");
      setTickers(options.draft.tickers ?? []);
      setKeywords(options.draft.keywords ?? []);
      setSelectedTags(
        options.draft.tags
          ? hydrateSelectedTags(options.draft.tags).initial
          : []
      );
      setColor(options.draft.color ?? "#FF0000");
      setPriority(options.draft.priority ?? 1);
      setNotifications(emptyNotifications());
    } else {
      setName("");
      setTickers([]);
      setKeywords([]);
      setSelectedTags([]);
      setColor("#FF0000");
      setPriority(1);
      setNotifications(emptyNotifications());
    }
    setErrors({});
    setImportedWatchlistIds(new Set());
  }, [options.editingPortfolio, options.editingTagWatchlist, options.draft]);

  // Clear all form content (keeps name)
  /**
   * Adds a resolved term, letting the lookup decide where it belongs.
   *
   * An instrument becomes a ticker (stored in its plain form, so AAPL.O and
   * "Apple" converge); anything else becomes a keyword. Either may carry a tag,
   * which is what makes the entry match on tagged feeds as well as by text.
   */
  const addResolvedTerm = useCallback((resolved: ResolvedTerm) => {
    if (resolved.ticker) {
      setTickers((prev) =>
        prev.includes(resolved.ticker!) ? prev : [...prev, resolved.ticker!]
      );
    } else if (resolved.keyword) {
      setKeywords((prev) =>
        prev.includes(resolved.keyword!) ? prev : [...prev, resolved.keyword!]
      );
    }
    if (resolved.tagId != null) {
      const key = resolved.ticker ?? resolved.keyword ?? resolved.input;
      setTermTags((prev) => ({ ...prev, [key]: String(resolved.tagId) }));
    }
  }, []);

  /** Drops a term's topic pairing, keeping the word itself. */
  const dropTermTag = useCallback((term: string) => {
    setTermTags((prev) => {
      const next = { ...prev };
      delete next[term];
      return next;
    });
  }, []);

  const clearAll = useCallback(() => {
    setTickers([]);
    setKeywords([]);
    setSelectedTags([]);
    setImportedWatchlistIds(new Set());
    setErrors({});
  }, []);

  // Re-populate form when the editing target or draft changes
  useEffect(() => {
    reset();
  }, [options.editingPortfolio, options.editingTagWatchlist, options.draft]); // eslint-disable-line react-hooks/exhaustive-deps

  // Resolve display names for any tag IDs not present in menuCategories
  useEffect(() => {
    const t = options.editingTagWatchlist;
    if (!t) return;
    const { unknownIds } = hydrateSelectedTags(t.tags);
    if (unknownIds.length === 0) return;

    let cancelled = false;
    getTagsByIds(unknownIds.join(" "))
      .then((tagDetails) => {
        if (cancelled || !tagDetails) return;
        setSelectedTags((prev) =>
          prev.map((cat) => {
            const tagId = String(cat.id);
            const detail = tagDetails[tagId];
            if (!detail) return cat;
            const primary = detail.labels?.find((l) => l.primary);
            const resolved = primary?.name || detail.labels?.[0]?.name;
            return resolved ? { ...cat, title: resolved } : cat;
          })
        );
      })
      .catch((err) => {
        console.error("Failed to fetch tag names:", err);
      });

    return () => {
      cancelled = true;
    };
  }, [options.editingTagWatchlist]);

  // Track which Newsquawk watchlists are imported (for clean removal)
  const [importedWatchlistIds, setImportedWatchlistIds] = useState<Set<number>>(
    new Set()
  );

  const keywordsSize = validateKeywordsSize(keywords);
  const termsTotal = validateTermsTotal(tickers, keywords);

  // Import tickers/keywords from a Newsquawk watchlist
  const importFromWatchlist = useCallback(
    (portfolioId: number) => {
      const portfolio = portfolios.find((p) => p.id === portfolioId);
      if (!portfolio) return;

      // Take everything the source holds, whatever it holds.
      //
      // This used to copy only tickers in "ticker" mode and only keywords in
      // "keyword" mode, so importing a keyword list into a ticker list silently
      // brought across nothing — the list appeared to tick and did no work. The
      // destination has no type any more, so neither should the import.
      const newTickers = portfolio.tickers.filter((t) => !tickers.includes(t));
      if (newTickers.length > 0) setTickers((prev) => [...prev, ...newTickers]);

      const newKeywords = (portfolio.keywords ?? []).filter(
        (k) => !keywords.includes(k)
      );
      if (newKeywords.length > 0)
        setKeywords((prev) => [...prev, ...newKeywords]);

      setImportedWatchlistIds((prev) => new Set(prev).add(portfolioId));
    },
    [portfolios, tickers, keywords]
  );

  // Remove imported tickers/keywords from a Newsquawk watchlist
  const removeImportedWatchlist = useCallback(
    (portfolioId: number) => {
      const portfolio = portfolios.find((p) => p.id === portfolioId);
      if (!portfolio) return;

      // Symmetrical with the import above: remove whatever it brought.
      const tickersToRemove = new Set(portfolio.tickers);
      setTickers((prev) => prev.filter((t) => !tickersToRemove.has(t)));

      const keywordsToRemove = new Set(portfolio.keywords ?? []);
      setKeywords((prev) => prev.filter((k) => !keywordsToRemove.has(k)));

      setImportedWatchlistIds((prev) => {
        const next = new Set(prev);
        next.delete(portfolioId);
        return next;
      });
    },
    [portfolios]
  );

  // Toggle a Newsquawk watchlist import
  const toggleWatchlistImport = useCallback(
    (portfolioId: number) => {
      if (importedWatchlistIds.has(portfolioId)) {
        removeImportedWatchlist(portfolioId);
      } else {
        importFromWatchlist(portfolioId);
      }
    },
    [importedWatchlistIds, importFromWatchlist, removeImportedWatchlist]
  );

  // Toggle a tag
  const toggleTag = useCallback((tag: Category) => {
    setSelectedTags((prev) => {
      const exists = prev.some((t) => t.id === tag.id);
      return exists ? prev.filter((t) => t.id !== tag.id) : [...prev, tag];
    });
    setErrors((e) => ({ ...e, tags: "" }));
  }, []);

  // Manual ticker/keyword add
  const addTicker = useCallback(
    (value: string) => {
      const trimmed = value.trim().toUpperCase();
      if (trimmed && !tickers.includes(trimmed)) {
        setTickers((prev) => [...prev, trimmed]);
        setErrors((e) => ({ ...e, tickers: "" }));
      }
    },
    [tickers]
  );

  const addTickers = useCallback((values: string[]) => {
    if (values.length === 0) return;
    setTickers((prev) => {
      const seen = new Set(prev);
      const next = [...prev];
      for (const v of values) {
        if (!seen.has(v)) {
          seen.add(v);
          next.push(v);
        }
      }
      return next;
    });
    setErrors((e) => ({ ...e, tickers: "" }));
  }, []);

  const removeTicker = useCallback((value: string) => {
    setTickers((prev) => prev.filter((t) => t !== value));
  }, []);

  const addKeyword = useCallback(
    (value: string) => {
      const trimmed = value.trim();
      if (trimmed && !keywords.includes(trimmed)) {
        setKeywords((prev) => [...prev, trimmed]);
      }
    },
    [keywords]
  );

  const addKeywords = useCallback((values: string[]) => {
    if (values.length === 0) return;
    setKeywords((prev) => {
      const seen = new Set(prev);
      const next = [...prev];
      for (const v of values) {
        if (!seen.has(v)) {
          seen.add(v);
          next.push(v);
        }
      }
      return next;
    });
  }, []);

  const removeKeyword = useCallback((value: string) => {
    setKeywords((prev) => prev.filter((k) => k !== value));
  }, []);

  /**
   * Resolves a batch and files each entry the way typing it would.
   *
   * Every route into a watchlist should produce the same record. Typing went
   * through resolveTerms and came out with a tag attached; an upload wrote raw
   * strings straight into tickers[], so the identical CSV produced an untagged
   * list that matched on text alone — worse on tagged feeds, and invisible
   * because both look the same on screen.
   *
   * `fallback` is the bucket to use if the tag service is unreachable. Keeping
   * what the user gave us beats discarding an upload because a lookup failed.
   */
  const addResolvedBatch = useCallback(
    async (values: string[], fallback: (v: string[]) => void) => {
      if (values.length === 0) return;
      setIsResolvingTerms(true);
      try {
        const { resolved } = await resolveTerms(values).catch(() => ({
          resolved: [],
        }));
        if (resolved.length === 0) {
          fallback(values);
          return;
        }
        resolved.forEach(addResolvedTerm);
      } finally {
        setIsResolvingTerms(false);
      }
    },
    [addResolvedTerm]
  );

  /**
   * A ticker CSV produces tickers — every row, whatever the lookup says.
   *
   * Resolution still runs, because it is what recovers a tag and turns "Apple"
   * into AAPL. But it no longer decides *placement*: it used to fall back to
   * tickers only when the whole file resolved to nothing, so a file that
   * partly resolved put its unresolved rows in `keywords` and quietly produced
   * a mixed list from something the user labelled as tickers.
   *
   * Placement matters on the firehose, where `tickers[]` is an exact field
   * lookup and keywords are text. Someone uploading a book of symbols has told
   * us what they are; the lookup's job is to enrich that, not overrule it.
   */
  const handleCsvUpload = useCallback(
    (file: File) => {
      const reader = new FileReader();
      reader.onload = async (event) => {
        const text = event.target?.result as string;
        // parseCsvKeywords, not parseCsvTickers: the ticker parser upper-cases,
        // which would turn a company name in the file into a ticker called
        // "APPLE" before the resolver ever saw the word.
        const rows = parseCsvKeywords(text);
        if (rows.length === 0) return;

        setIsResolvingTerms(true);
        try {
          const { resolved } = await resolveTerms(rows).catch(() => ({
            resolved: [],
          }));
          const byInput = new Map(
            resolved.map((r) => [r.input.trim().toUpperCase(), r])
          );

          const symbols: string[] = [];
          const tags: Record<string, string> = {};
          for (const row of rows) {
            const hit = byInput.get(row.trim().toUpperCase());
            // A resolved instrument contributes its plain symbol — "Apple"
            // becomes AAPL. Anything else keeps the row as written.
            const symbol = (hit?.ticker ?? row).trim().toUpperCase();
            if (!symbol) continue;
            symbols.push(symbol);
            if (hit?.tagId != null) tags[symbol] = String(hit.tagId);
          }

          addTickers(symbols);
          if (Object.keys(tags).length > 0) {
            setTermTags((prev) => ({ ...prev, ...tags }));
          }
        } finally {
          setIsResolvingTerms(false);
        }
      };
      reader.readAsText(file);
    },
    [addTickers]
  );

  // CSV upload for keywords
  const handleKeywordCsvUpload = useCallback(
    (file: File) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const text = event.target?.result as string;
        void addResolvedBatch(parseCsvKeywords(text), addKeywords);
      };
      reader.readAsText(file);
    },
    [addResolvedBatch, addKeywords]
  );

  /**
   * What has changed on a Newsquawk list opened from the library, split by
   * what saving it has to do.
   *
   * `content` — name, terms or alerts — needs a list of the user's own: the
   * library list can't be changed, and alerts live on the record. `look` —
   * colour and priority — doesn't: those can be kept as this user's override
   * of the library list, which is what the library browser used to offer.
   * Neither: nothing to save. Saving an untouched copy gave the user a
   * duplicate that stops getting the desk's updates, of a list they could
   * already use as it is.
   */
  const libraryChanges = useMemo(() => {
    const draft = options.draft;
    if (isEditing || !draft?.basedOn) return null;
    const content = !(
      name.trim() === (draft.name ?? "").trim() &&
      sameMembers(tickers, draft.tickers ?? []) &&
      sameMembers(keywords, draft.keywords ?? []) &&
      sameMembers(
        selectedTags.map((t) => String(t.id)),
        draft.tags ?? []
      ) &&
      importedWatchlistIds.size === 0 &&
      JSON.stringify(notifications) === JSON.stringify(emptyNotifications())
    );
    const look =
      color !== (draft.color ?? "#FF0000") ||
      priority !== (draft.priority ?? 1);
    return { content, look };
  }, [
    options.draft,
    isEditing,
    name,
    tickers,
    keywords,
    selectedTags,
    importedWatchlistIds,
    color,
    priority,
    notifications,
  ]);
  const libraryId = libraryChanges ? options.draft?.libraryId : undefined;
  const copyUnchanged =
    !!libraryChanges && !libraryChanges.content && !libraryChanges.look;
  /** Only colour or priority differ, on a list they can be kept for. */
  const lookOnly =
    !!libraryChanges &&
    !libraryChanges.content &&
    libraryChanges.look &&
    libraryId != null;

  const overrides = useSyncExternalStore(
    subscribeToPrebuiltOverrides,
    getPrebuiltOverridesSnapshot,
    getPrebuiltOverridesSnapshot
  );
  const hasLibraryOverride = libraryId != null && !!overrides[libraryId];

  /** Back to the colour and priority the desk set. */
  const resetLibraryLook = useCallback((): SavedWatchlist | undefined => {
    if (libraryId == null) return undefined;
    clearPrebuiltOverride(libraryId);
    toast.success(`“${options.draft?.basedOn}” is back to Newsquawk's colour and priority`);
    return { kind: "watchlist", id: libraryId };
  }, [libraryId, options.draft?.basedOn]);

  // Submit (create or update)
  /** Resolves to what was saved, or undefined if nothing was. */
  const handleSubmit = useCallback(async (): Promise<SavedWatchlist | undefined> => {
    if (copyUnchanged) return undefined;
    // Colour and priority only: keep them for this user; nothing is created.
    if (lookOnly && libraryId != null) {
      setPrebuiltOverride(libraryId, { color, priority });
      toast.success(`Colour and priority saved for “${options.draft?.basedOn}”`);
      const saved: SavedWatchlist = { kind: "watchlist", id: libraryId };
      options.onCreated?.(saved);
      return saved;
    }
    const errs: Record<string, string> = {};
    if (!name.trim()) errs.name = "Name is required";

    // Content, not content-of-a-particular-type.
    //
    // These used to demand tickers of a "ticker" watchlist and keywords of a
    // "keyword" one. The user no longer picks a type before typing — the tag
    // lookup decides what each entry is — so a list of topics typed into a
    // watchlist that happened to open in ticker mode failed to save with
    // "At least one ticker is required", against a form that was full.
    if (options.mode === "tag") {
      if (selectedTags.length === 0) errs.tags = "At least one tag is required";
    } else {
      const isEmpty =
        tickers.length === 0 &&
        keywords.length === 0 &&
        importedWatchlistIds.size === 0;
      if (isEmpty) errs.tickers = "Add at least one ticker, company or topic";
      if (keywords.length > 0 && !keywordsSize.ok) {
        errs.keywords = keywordsSize.error ?? "Keyword limit exceeded";
      }
      // Tickers were never counted, so a pasted index could grow a list without
      // limit down the one side nothing checked.
      if (!termsTotal.ok) {
        errs.tickers = termsTotal.error ?? "Too many entries";
      }
    }

    setErrors(errs);
    if (Object.keys(errs).length > 0) return undefined;

    setIsSubmitting(true);

    /**
     * Notes ride along on every save, create or edit — the desk backfills them
     * onto lists that already exist. Empty strings are sent as null so
     * clearing a note actually clears it rather than storing "".
     *
     * A non-desk user never sets these (the UI is gated), so both spreads are
     * empty objects and the payloads are byte-identical to before.
     */
    const deskNotes = {
      description: description.trim() || null,
      when_to_use: whenToUse.trim() || null,
      categories,
    };
    // Create-only: ignored by the server on PATCH.
    const deskOwner = publishToLibrary
      ? ({ owner_type: "NEWSQUAWK" } as const)
      : {};

    let saved: SavedWatchlist | undefined;
    try {
      if (options.mode === "tag") {
        const tags = selectedTags.map((t) => String(t.id));
        if (isEditing && editId != null) {
          await tagUpdateMutation.mutateAsync({
            id: editId,
            data: {
              name: formatListName(name),
              tags,
              notifications,
              ...deskNotes,
            },
          });
          toast.success("Watchlist updated");
          saved = { kind: "tag", id: editId, tags };
        } else {
          const response = await tagCreateMutation.mutateAsync({
            name: formatListName(name),
            tags,
            notifications,
            ...deskOwner,
            ...deskNotes,
          });
          toast.success("Watchlist created");
          // Previously nothing, so a new tag watchlist was never previewed.
          // Every successful branch sets `saved`: callers read its presence
          // as "that worked".
          saved = { kind: "tag", id: Number(response.data?.id), tags };
          trackFeatureUsage({
            feature_name: "watchlist",
            feature_action: "create",
            feature_detail: "tag",
          });
        }
      } else if (isEditing && editId != null) {
        await updateMutation.mutateAsync({
          ...(options.editingPortfolio as Portfolio),
          name: formatListName(name),
          tickers,
          keywords,
          tags: Object.values(termTags),
          color,
          priority,
          notifications,
          ...deskNotes,
        });
        // See DetailPaneEditor: a stale local override would mask the colour
        // and priority the desk just saved to the server.
        if (
          (options.editingPortfolio as Portfolio).owner_type === "NEWSQUAWK"
        ) {
          clearPrebuiltOverride(editId);
        }
        toast.success("Watchlist updated");
        saved = { kind: "watchlist", id: editId };
      } else if (!authenticated) {
        /**
         * Guided demo: nothing is written.
         *
         * POST /portfolios needs an account and would 403, so the button that
         * ends the builder's most important step failed for exactly the
         * audience the demo sends there. Nothing to create and nothing to
         * clean up either — the demo watchlist is already in the panels via
         * demoWatchlist.ts, so "creating" it is a no-op that reports the truth
         * about what would happen with an account.
         */
        toast.success("Watchlist created");
        markWatchlistCreated();
        saved = { kind: "watchlist", id: -1 };
        trackFeatureUsage({
          feature_name: "watchlist",
          feature_action: "create",
          feature_detail: "demo",
        });
      } else {
        // A placeholder: Portfolio requires an id, but the server ignores it
        // and allocates its own, which is the one to preview.
        const created = await createMutation.mutateAsync({
          id: 0,
          name: formatListName(name),
          tickers,
          keywords,
          tags: Object.values(termTags),
          color,
          priority,
          notifications,
          customizations: { includeRelatedHeadlines: false },
          owner_type: publishToLibrary ? "NEWSQUAWK" : "USER",
          ...deskNotes,
        } as Portfolio);
        toast.success("Watchlist created");
        saved = { kind: "watchlist", id: created.id };
        trackFeatureUsage({
          feature_name: "watchlist",
          feature_action: "create",
          feature_detail: options.mode,
        });
      }
      options.onCreated?.(saved);
      return saved;
    } catch {
      toast.error(
        isEditing ? "Failed to update watchlist" : "Failed to create watchlist"
      );
      return undefined;
    } finally {
      setIsSubmitting(false);
    }
  }, [
    name,
    tickers,
    keywords,
    termTags,
    selectedTags,
    color,
    priority,
    notifications,
    publishToLibrary,
    description,
    whenToUse,
    categories,
    options.mode,
    importedWatchlistIds,
    keywordsSize,
    portfolios,
    createMutation,
    updateMutation,
    tagCreateMutation,
    tagUpdateMutation,
    isEditing,
    editId,
    options.editingPortfolio,
    options.editingTagWatchlist,
    options.onCreated,
    copyUnchanged,
    lookOnly,
    libraryId,
  ]);

  // Delete (edit mode only)
  const handleDelete = useCallback(async () => {
    if (!isEditing || editId == null) return;
    setIsSubmitting(true);
    try {
      if (options.mode === "tag") {
        await tagDeleteMutation.mutateAsync(editId);
        toast.success("Watchlist deleted");
      } else {
        await deleteMutation.mutateAsync(String(editId));
        toast.success("Watchlist deleted");
      }
      options.onDeleted?.();
    } catch {
      toast.error("Failed to delete watchlist");
    } finally {
      setIsSubmitting(false);
    }
  }, [
    isEditing,
    editId,
    options.mode,
    deleteMutation,
    tagDeleteMutation,
    options.onDeleted,
  ]);

  return {
    // Form state
    name,
    setName,
    tickers,
    keywords,
    selectedTags,
    color,
    setColor,
    priority,
    setPriority,
    notifications,
    setNotifications,
    errors,
    isSubmitting,
    isResolvingTerms,

    // Desk-only (see useIsDeskUser)
    publishToLibrary,
    setPublishToLibrary,
    description,
    setDescription,
    whenToUse,
    setWhenToUse,
    categories,
    setCategories,
    isNewsquawkOwned: editingOwnerType === "NEWSQUAWK",
    /** The Newsquawk list being copied, if this is one — see the draft. */
    basedOn: isEditing ? undefined : options.draft?.basedOn,
    copyUnchanged,
    /** Colour and priority can be kept for this user — see libraryChanges. */
    libraryLookable: libraryId != null,
    /** Name, terms or alerts differ, so saving makes a new list. */
    libraryContentChanged: !!libraryChanges?.content,
    lookOnly,
    hasLibraryOverride,
    resetLibraryLook,

    // Actions
    addTicker,
    addTickers,
    removeTicker,
    addKeyword,
    addKeywords,
    removeKeyword,
    addResolvedTerm,
    dropTermTag,
    termTags,
    toggleTag,
    handleCsvUpload,
    handleKeywordCsvUpload,
    toggleWatchlistImport,
    importedWatchlistIds,
    handleSubmit,
    handleDelete,
    reset,
    clearAll,
    isEditing,

    // Derived
    keywordsSize,
    termsTotal,
    MAX_KEYWORDS_COUNT,
    MAX_KEYWORDS_TOTAL_CHARS,
    MAX_TERMS_TOTAL,
    mode: options.mode,
    portfolios,
  };
}

export type UseWatchlistCreatorReturn = ReturnType<typeof useWatchlistCreator>;
